<template>
  <div>
    <h1>{{ $t("travelService.orderServicesCustomerForm.title") }}</h1>

    <v-alert :value="isInsurance" color="error darken-4" outlined>
      <span v-html="$t('travelService.insuranceWarning')" />
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="contextEditedTravelServiceOrder.travelServiceOrderItems"
      :loading="$store.getters['Ajax/loading'] ? 'accent' : false"
      hide-default-footer
      :footer-props="{ 'items-per-page-options': [{ text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }] }"
    >
      <template #no-data>
        <div class="white--text font-weight-bold info pa-5" style="width: 100%;">
          {{ $t("travelService.orderServicesCustomerForm.noData") }}
        </div>
      </template>
      <template #item="props">
        <tr>
          <td>{{ $t("travelService.types." + props.item.type) }}</td>
          <td>
            <order-services-request-dates :order-service-item="props.item" />
          </td>
          <td>
            <span v-if="props.item.type === 'hotel'">
              {{ allRoomTypes().find(v => v.value === props.item.travelServiceOrderItemHotel.room_type).text }}
              <br />
              <b>{{ $store.getters["Location/getLocationText"](props.item.travelServiceOrderItemHotel.place_id) }}</b>
            </span>
            <span v-if="props.item.type === 'flight'">
              {{ $t("travelService.orderServicesCustomerForm.fromPlace") }}
              <b>{{ $store.getters["Location/getLocationText"](props.item.travelServiceOrderItemFlight.start_place_id) }}</b>
              {{ $t("travelService.orderServicesCustomerForm.toPlace") }}
              <b>{{ $store.getters["Location/getLocationText"](props.item.travelServiceOrderItemFlight.end_place_id) }}</b>
              <br />
              {{ $t("travelService.orderServicesCustomerForm.class") }}:
              <b>{{ allFlightClassTypes().find(v => v.value === props.item.travelServiceOrderItemFlight.seat_class).text }}</b>
            </span>
            <span v-if="props.item.type === 'rail'">
              {{ $t("travelService.orderServicesCustomerForm.fromPlace") }}
              <b>{{ $store.getters["Location/getLocationText"](props.item.travelServiceOrderItemRail.start_place_id, "train_station") }}</b>
              {{ $t("travelService.orderServicesCustomerForm.toPlace") }}
              <b>{{ $store.getters["Location/getLocationText"](props.item.travelServiceOrderItemRail.end_place_id, "train_station") }}</b>
              <br />
              {{ $t("travelService.orderServicesCustomerForm.class") }}:
              <b>{{ allRailClassTypes().find(v => v.value === props.item.travelServiceOrderItemRail.seat_class).text }}</b>
            </span>
            <span v-if="props.item.type === 'car'">
              {{ $t("travelService.orderServicesCustomerForm.fromPlaceCar") }}
              <b>{{ $store.getters["Location/getLocationText"](props.item.travelServiceOrderItemCar.start_place_id) }}</b>
              <span v-if="props.item.travelServiceOrderItemCar.end_place_id != null && props.item.travelServiceOrderItemCar.end_place_id.length > 0">
                {{ $t("travelService.orderServicesCustomerForm.toPlaceCar") }}
                <b>{{ $store.getters["Location/getLocationText"](props.item.travelServiceOrderItemCar.end_place_id) }}</b></span
              >
              <br />
              {{ $t("travelService.orderServicesCustomerForm.carClass") }}:
              <b>{{ allCarTypes().find(v => v.value === props.item.travelServiceOrderItemCar.car_class).text }}</b>
            </span>
            <span v-if="props.item.type === 'other'">
              {{ $t("travelService.orderServicesCustomerForm.service") }}:
              <b>{{ props.item.params }}</b>
            </span>
            <span v-if="props.item.type === 'insurance'">
              <b>{{ $store.getters["Location/getLocationText"](props.item.travelServiceOrderItemInsurance.place_id) }}</b>
            </span>
            {{ props.item.comment }} <br />
            {{ $t("travelService.orderServicesCustomerForm.user") }}:
            <span :key="user.passenger_id" v-for="user in props.item.travelServiceOrderItemPassengers">
              <b>{{ user.name }} {{ user.surname }}</b
              >,
            </span>
            <br />
            {{ $t("travelService.orderServicesCustomerForm.costCentre") }}:
            <b>{{ props.item.cost_centre }}</b>
          </td>
          <td v-if="canBeEdited">
            <v-btn @click="edit(props.item)" icon>
              <v-icon>edit</v-icon>
            </v-btn>
            <v-tooltip :disabled="!(typeof props.item.status != 'undefined' && props.item.status !== 1)" bottom>
              <template #activator="{on}">
                <v-btn :disabled="typeof props.item.status != 'undefined' && props.item.status !== 1" @click.native="removeItem(props.item)" icon v-on="on">
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              {{ $t("travelService.orderServicesCustomerForm.attachedResponse") }}
            </v-tooltip>
          </td>
          <td v-if="!canBeEdited" />
        </tr>
      </template>

      <template slot="footer">
        <td colspan="100%" v-if="!canBeEdited">
          <h4>
            {{ $t("travelService.orderServicesCustomerForm.noPermissionsForAdding") }}
          </h4>
        </td>
        <div style="text-align: center;" v-if="canBeEdited">
          <div
            @click="$store.dispatch('TravelService/createFlightItem')"
            class="pa-4 text-md-center elevation-5 ma-3 add-service-buttons"
            v-if="$store.getters['userProperties'].system_settings.travel_service_flight_available"
          >
            <div style="padding-left: 37px;">
              <v-icon color="primary" style="font-size: 60px;">fa-plane</v-icon>
              <v-btn color="success" x-small fab class="plus-button">
                <v-icon>fa-plus</v-icon>
              </v-btn>
            </div>
            <h3>
              {{ $t("travelService.orderServicesCustomerForm.orderFlight") }}
            </h3>
          </div>
          <div
            @click="$store.dispatch('TravelService/createRailItem')"
            class="pa-4 text-md-center elevation-5 ma-3 add-service-buttons"
            v-if="$store.getters['userProperties'].system_settings.travel_service_rail_available"
          >
            <div style="padding-left: 37px;">
              <v-icon color="primary" style="font-size: 60px;">fa-train</v-icon>
              <v-btn color="success" x-small fab class="plus-button">
                <v-icon>fa-plus</v-icon>
              </v-btn>
            </div>
            <h3>
              {{ $t("travelService.orderServicesCustomerForm.orderRail") }}
            </h3>
          </div>
          <div
            @click="$store.dispatch('TravelService/createCarItem')"
            class="pa-4 text-md-center elevation-5 ma-3 add-service-buttons"
            v-if="$store.getters['userProperties'].system_settings.travel_service_car_available"
          >
            <div style="padding-left: 37px;">
              <v-icon color="primary" style="font-size: 60px;">fa-car</v-icon>
              <v-btn color="success" x-small fab class="plus-button">
                <v-icon>fa-plus</v-icon>
              </v-btn>
            </div>
            <h3>
              {{ $t("travelService.orderServicesCustomerForm.orderCar") }}
            </h3>
          </div>
          <div
            @click="$store.dispatch('TravelService/createHotelItem')"
            class="pa-4 text-md-center elevation-5 ma-3 add-service-buttons"
            v-if="$store.getters['userProperties'].system_settings.travel_service_hotel_available"
          >
            <div style="padding-left: 37px;">
              <v-icon color="primary" style="font-size: 60px;">fa-hotel</v-icon>
              <v-btn color="success" x-small fab class="plus-button">
                <v-icon>fa-plus</v-icon>
              </v-btn>
            </div>
            <h3>
              {{ $t("travelService.orderServicesCustomerForm.orderHotel") }}
            </h3>
          </div>
          <div @click="$store.dispatch('TravelService/createOtherItem')" class="pa-4 text-md-center elevation-5 ma-3 add-service-buttons">
            <div style="padding-left: 37px;">
              <v-icon color="primary" style="font-size: 60px;">fa-suitcase-rolling</v-icon>
              <v-btn color="success" x-small fab class="plus-button">
                <v-icon>fa-plus</v-icon>
              </v-btn>
            </div>
            <h3>
              {{ $t("travelService.orderServicesCustomerForm.orderOthers") }}
            </h3>
          </div>
          <div @click="$store.dispatch('TravelService/createInsuranceItem')" class="pa-4 text-md-center elevation-5 ma-3 add-service-buttons">
            <div style="padding-left: 37px;">
              <v-icon color="primary" style="font-size: 60px;">fa-clinic-medical</v-icon>
              <v-btn color="success" x-small fab class="plus-button">
                <v-icon>fa-plus</v-icon>
              </v-btn>
            </div>
            <h3>
              {{ $t("travelService.insurance") }}
            </h3>
          </div>
          <div
            @click="$router.push('/travel-service/pkp-intercity')"
            class="pa-4 text-md-center elevation-5 ma-3 add-service-buttons"
            v-if="$store.state.Auth.isWnt === false"
          >
            <div style="padding-left: 37px;">
              <v-icon color="primary" style="font-size: 60px;">fa-suitcase-rolling</v-icon>
              <v-btn color="success" x-small fab class="plus-button">
                <v-icon>fa-plus</v-icon>
              </v-btn>
            </div>
            <h3>
              PKP Intercity
            </h3>
          </div>
        </div>
      </template>
    </v-data-table>

    <v-checkbox :label="$t('travelService.asSeparated')" v-if="contextEditedTravelServiceOrder.id === null" v-model="asSeparated" />

    <h2>{{ $t("travelService.orderServicesCustomerForm.contracting") }}:</h2>
    <v-row dense>
      <v-col class="pa-2" cols="4">
        <v-text-field :label="$t('travelService.orderServicesCustomerForm.orderBy')" readonly v-model="contextUser" />
        <v-textarea :label="$t('travelService.orderServicesCustomerForm.company')" readonly v-model="company" />
        <v-select
          :items="availableCompanies"
          :label="$t('travelService.orderServicesCustomerForm.company')"
          @input="updateVuexState"
          item-text="name"
          item-value="id"
          v-if="availableCompanies.length > 1"
          v-model="contextEditedTravelServiceOrder.company_id"
        />
      </v-col>
      <v-col class="pa-2" cols="8">
        <v-textarea
          :label="$t('travelService.orderServicesCustomerForm.commentAboutWholeOrder')"
          @input="updateVuexState"
          height="228px"
          outlined
          v-model="contextEditedTravelServiceOrder.comment"
        />
        <v-row v-if="isReInvoicingForExternalContractorModuleEnabled" dense>
          <v-col class="pr-2" cols="6">
            <v-autocomplete
              :item-disabled="v => !v.is_enabled"
              :items="$store.state.TravelService.externalCustomers"
              :label="$t('delegation.form.fields.externalCustomer.name')"
              clearable
              item-text="name"
              item-value="id"
              v-model="contextEditedTravelServiceOrder.external_customer_id"
            />
          </v-col>
          <v-col class="pl-2" cols="6">
            <v-autocomplete
              :hint="
                contextEditedTravelServiceOrder.project_id === null && projectsForExternalCustomer.length > 0
                  ? ''
                  : $t('global.orderNumber') + ': ' + externalOrderNumber
              "
              :item-disabled="v => !v.is_enabled"
              :items="projectsForExternalCustomer"
              :label="$t('delegation.form.fields.project.name')"
              clearable
              item-text="name"
              item-value="id"
              persistent-hint
              v-model="contextEditedTravelServiceOrder.project_id"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-form ref="additionalParamsForm">
      <template v-if="contextEditedTravelServiceOrder.additional_parameters !== null && contextEditedTravelServiceOrder.additional_parameters.length > 0">
        <h2>{{ $t("travelService.additionalParametersList") }}:</h2>
        <v-row dense>
          <v-col :key="additionalParameter.ordinate" class="pa-2" v-for="additionalParameter of contextEditedTravelServiceOrder.additional_parameters" cols="4">
            <v-text-field
              :label="additionalParameter.name"
              :rules="[
                v =>
                  additionalParameter.is_required === 0 || additionalParameter.is_required === '0' || (v != null && v.length > 0) || $t('global.error.required')
              ]"
              @input="updateVuexState"
              v-model="additionalParameter.value"
            />
          </v-col>
        </v-row>
      </template>
    </v-form>

    <v-row>
      <v-spacer />
      <v-btn :disabled="$store.getters['Ajax/loading']" @click="$emit('cancel')" color="blue darken-1" text>
        {{ $t("global.cancel") }}
      </v-btn>
      <v-btn :disabled="$store.getters['Ajax/loading']" @click="save" class="success" v-if="canBeEdited">
        {{ $t("travelService.orderServicesCustomerForm.orderButton") }}
      </v-btn>
    </v-row>
    <order-service-item-dialog />
  </div>
</template>

<script>
import { allCarTypes, allFlightClassTypes, allRailClassTypes, allRoomTypes } from "../../staticData/TravelServiceTypes";
import OrderServicesRequestDates from "./OfferPresentation/Partial/OrderServicesRequestDates";
import OrderServiceItemDialog from "./OrderServiceItemDialog";
import TravelServiceOrder from "../../models/travelOrder/travelServiceOrder";
import vueMainInstance from "../../main";

export default {
  name: "OrderServices",
  components: {
    OrderServiceItemDialog,
    OrderServicesRequestDates
  },
  data: () => ({
    comment: null,
    company_id: null,
    availableCompanies: [],
    contextEditedTravelServiceOrder: new TravelServiceOrder(),
    asSeparated: false
  }),
  watch: {
    asSeparated(val) {
      this.$store.commit("TravelService/setAsSeparated", val);
    },
    vuexEditedTravelService: {
      handler(val) {
        this.contextEditedTravelServiceOrder = val;
      },
      deep: true
    }
  },
  methods: {
    isFormChanged() {
      return JSON.stringify(this.$store.state.TravelService.editedTravelService) !== JSON.stringify(this.$store.state.TravelService.oldTravelService);
    },
    allCarTypes,
    allFlightClassTypes,
    allRailClassTypes,
    allRoomTypes,
    updateVuexState() {
      this.$store.commit("TravelService/changeEditedTravelService", this.contextEditedTravelServiceOrder);
    },
    removeItem(item) {
      let relatedItems = this.contextEditedTravelServiceOrder.travelServiceOrderItems.filter(v => v.related_sequence === item.sequence);
      if (relatedItems.length > 0) {
        for (let relatedItem of relatedItems) {
          if (relatedItem.type === "insurance") {
            this.contextEditedTravelServiceOrder.travelServiceOrderItems.splice(
              this.contextEditedTravelServiceOrder.travelServiceOrderItems.indexOf(relatedItem),
              1
            );
          }
        }
      }
      this.contextEditedTravelServiceOrder.travelServiceOrderItems.splice(this.contextEditedTravelServiceOrder.travelServiceOrderItems.indexOf(item), 1);
      this.updateVuexState();
    },
    edit(item) {
      this.$store.commit("TravelService/setEditedTravelServiceItem", item);
      this.$store.commit("TravelService/setOldTravelServiceItem", item);
    },
    save() {
      // let self = this;
      if (this.contextEditedTravelServiceOrder.travelServiceOrderItems.length === 0) {
        this.$store.dispatch("snackbarPush", {
          text: this.$t("travelService.orderServicesCustomerForm.canNotSaveEmptyOrder"),
          color: "error",
          position: "down"
        });
      } else if (this.$refs.additionalParamsForm.validate() !== true) {
        this.$store.dispatch("snackbarPush", {
          text: this.$t("travelService.orderServicesCustomerForm.canNotSaveRequiredAdditionalParamsMissing"),
          color: "error",
          position: "down"
        });
      } else {
        this.$store.dispatch("TravelService/saveEditedService").then(id => {
          this.$emit("saved", id);
        });
      }
    },
    beforeRouteLeave(from, to, next) {
      if (from.fullPath === "/logout") {
        next();
      } else {
        if (this.isFormChanged()) {
          let result = this.$store.dispatch("confirmDialog", {
            content: this.$t("global.saveBeforeExit.content"),
            title: this.$t("global.saveBeforeExit.title"),
            customButtons: [
              { text: this.$t("global.cancel"), value: "cancel" },
              { text: this.$t("global.no"), value: "no" },
              { text: this.$t("global.yes"), color: "primary", value: "yes" }
            ]
          });
          result.then(chosen => {
            if (chosen === "cancel") {
              next(false);
            } else if (chosen === "no") {
              next();
            } else if (chosen === "yes") {
              this.save();
            }
          });
        } else {
          next();
        }
      }
    }
  },
  computed: {
    isInsurance() {
      for (let item of this.contextEditedTravelServiceOrder.travelServiceOrderItems) {
        if (item.type === "insurance") {
          return true;
        }
      }
      return false;
    },
    externalOrderNumber() {
      let selectedProject = this.projectsForExternalCustomer.find(v => v.id === this.contextEditedTravelServiceOrder.project_id);
      if (typeof selectedProject === "undefined") return "";
      return selectedProject.order_number === null ? this.$t("global.none") : selectedProject.order_number;
    },
    projectsForExternalCustomer() {
      let externalCustomer = this.$store.state.TravelService.externalCustomers.find(v => v.id === this.contextEditedTravelServiceOrder.external_customer_id);
      if (typeof externalCustomer !== "undefined") {
        return externalCustomer.projects;
      }
      return [];
    },
    isReInvoicingForExternalContractorModuleEnabled() {
      return this.$store.getters["userProperties"].system_settings.external_customers_module === 2;
    },
    headers() {
      return [
        {
          text: this.$t("travelService.orderServicesCustomerForm.headers.type"),
          value: "type",
          sortable: false
        },
        {
          text: this.$t("travelService.orderServicesCustomerForm.headers.date"),
          value: "date",
          sortable: false
        },
        {
          text: this.$t("travelService.orderServicesCustomerForm.headers.details"),
          value: "param",
          sortable: false
        },
        { text: "", value: "actions", sortable: false }
      ];
    },
    contextUser() {
      if (this.oldOrder == null) {
        return this.$store.state.Auth.userProperties.name + " " + this.$store.state.Auth.userProperties.surname;
      }
      return this.oldOrder.ordered_by_name;
    },
    canBeEdited() {
      return this.$store.getters["TravelService/hasPermissionForEdit"];
    },
    company() {
      let companyInfo = this.$store.state.TravelService.availableCompanies.find(v => v.id === this.$store.state.TravelService.editedTravelService.company_id);
      if (typeof companyInfo !== "undefined") {
        let company = companyInfo.name + " \n";
        if (companyInfo.tin != null) {
          company += this.$t("travelService.companyProperties.tin") + ": " + companyInfo.tin + "\n";
        }
        if (companyInfo.street_and_number != null && companyInfo.postal_code != null && companyInfo.city != null) {
          company +=
            this.$t("travelService.companyProperties.address") +
            ": \n" +
            companyInfo.street_and_number +
            "\n" +
            companyInfo.postal_code +
            " " +
            companyInfo.city;
        }
        return company;
      }
      return "";
    },
    vuexEditedTravelService() {
      return this.$store.state.TravelService.editedTravelService;
    }
  },
  created() {
    if (typeof this.$route.params.id !== "undefined" && !vueMainInstance.$route.fullPath.includes("delegation-order")) {
      this.$store.dispatch("TravelService/editTravelServiceOrder", this.$route.params.id);
    } else {
      this.$store.dispatch("TravelService/createNewTravelServiceOrder");
      this.contextEditedTravelServiceOrder = /*JSON.stringify(JSON.parse(*/ this.$store.state.TravelService.editedTravelService; /*))*/
    }
    this.$store.dispatch("TravelService/downloadAvailableCompanies").then(availableCompanies => {
      this.availableCompanies = availableCompanies;
    });
    this.$store.dispatch("TravelService/openTravelServiceOrder");
    //this.additionalParameters = this.$store.state.TravelService.editedTravelService.additional_parameters;

    this.hotelRequiredDialog = () =>
      this.$store.dispatch("confirmDialog", {
        content: this.$t("travelService.orderServicesCustomerForm.hotelRequiredDialog.content"),
        title: this.$t("travelService.orderServicesCustomerForm.hotelRequiredDialog.title")
      });
  }
};
</script>

<style scoped>
.add-service-buttons {
  background-color: rgb(245, 245, 245);
  width: 215px;
  display: inline-block;
  cursor: pointer;
}

.plus-button {
  position: relative;
  right: 15px;
  top: 15px;
}
</style>
